<template>
  <Teleport to="body">
    <transition name="modal" appear>
      <div v-if="visible" class="wx-qr-overlay">
      <div class="wx-qr-overlay-bg" @click="close"></div>
      <div class="wx-qr-modal">
        <div class="wx-qr-header">
          <h3>{{ mode === 'bind' ? '绑定微信' : (isMobile ? '微信快捷登录' : '微信扫码登录') }}</h3>
          <button class="close-btn" @click="close">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="wx-qr-brand">
          <img :src="`${assetsUrl}/4yshop/img/logo.png`" alt="云语匀雨" class="wx-qr-brand-logo" />
          <span class="wx-qr-brand-name">云语匀雨</span>
        </div>

        <div class="wx-qr-body">
          <!-- 生成中 -->
          <div v-if="loading" class="qr-state loading">
            <i class="fa-solid fa-circle-notch fa-spin"></i>
            <p>{{ isMobile ? '正在准备微信授权...' : '正在生成二维码...' }}</p>
          </div>

          <!-- 移动端：展示跳转小程序引导卡片 / 小程序码 -->
          <div v-else-if="isMobile && (schemeUrl || qrUrl || qrCodeBase64) && !isFinalState" class="mobile-jump-state">
            <template v-if="schemeUrl">
              <div class="mobile-wx-badge">
                <i class="fa-brands fa-weixin"></i>
              </div>
              <p class="mobile-jump-tip">
                即将跳转至微信小程序完成{{ mode === 'bind' ? '绑定授权' : '快捷登录' }}
              </p>

              <button class="mobile-jump-btn" @click="openWechatMiniApp">
                <i class="fa-brands fa-weixin"></i> 打开微信小程序授权
              </button>
            </template>

            <template v-else>
              <div class="qr-canvas-wrapper" :class="{ 'is-scanned': status === 'scanned' }">
                <img v-if="qrCodeBase64" :src="qrCodeBase64" class="qr-image" style="width: 220px; height: 220px;" />
                <canvas v-else ref="qrCanvas" class="qr-canvas"></canvas>
              </div>
              <p class="mobile-jump-tip" style="margin-top: 12px; margin-bottom: 8px; font-size: 14px;">
                请长按或使用 <span class="wx-brand">微信</span> 识别上方小程序码完成{{ mode === 'bind' ? '绑定' : '登录' }}
              </p>
            </template>

            <div class="qr-status-bar pending" style="margin-top: 12px;">
              <i class="fa-solid fa-clock"></i>
              <span>等待授权完成... {{ countdown }}s 后失效</span>
            </div>
          </div>

          <!-- PC端：二维码/小程序码展示 -->
          <div v-else-if="!isMobile && (qrUrl || qrCodeBase64) && !isFinalState" class="qr-state code">
            <div class="qr-canvas-wrapper" :class="{ 'is-scanned': status === 'scanned' }">
              <template v-if="status === 'scanned'">
                <div class="scanned-box">
                  <i class="fa-solid fa-circle-check scanned-icon"></i>
                  <p class="scanned-text">二维码已扫描</p>
                  <p class="scanned-subtext">请在手机微信上点击确认</p>
                </div>
              </template>
              <template v-else>
                <img v-if="qrCodeBase64" :src="qrCodeBase64" class="qr-image" style="width: 240px; height: 240px;" />
                <canvas v-else ref="qrCanvas" class="qr-canvas"></canvas>
                <div v-if="status === 'pending'" class="qr-refresh-hint">
                  <i class="fa-solid fa-qrcode"></i>
                </div>
              </template>
            </div>
            <p v-if="status !== 'scanned'" class="qr-tip">请使用 <span class="wx-brand">微信</span> 扫描上方{{ qrCodeBase64 ? '小程序码' : '二维码' }}</p>
            <p v-else class="qr-tip"><span class="wx-brand">微信</span> 已扫描，等待确认中...</p>
            <div class="qr-status-bar" :class="status">
              <template v-if="status === 'pending'">
                <i class="fa-solid fa-clock"></i>
                <span>等待扫码... {{ qrCodeBase64 ? '小程序码' : '二维码' }} {{ countdown }}s 后失效</span>
              </template>
              <template v-else-if="status === 'scanned'">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>手机端已扫码，等待用户确认...</span>
              </template>
            </div>
          </div>

          <!-- 成功 -->
          <div v-else-if="status === 'success'" class="qr-state success">
            <div class="state-icon success">
              <i class="fa-solid fa-circle-check"></i>
            </div>
            <p class="state-text">{{ successText }}</p>
          </div>

          <!-- 未绑定 -->
          <div v-else-if="status === 'unbound'" class="qr-state error">
            <div class="state-icon error">
              <i class="fa-solid fa-circle-exclamation"></i>
            </div>
            <p class="state-text">{{ errorText || '该微信账号暂未绑定云语账号' }}</p>
            <p class="state-hint" v-if="mode === 'login'">如果您还未注册账号，请先注册账号并在账户设置中绑定微信</p>
          </div>

          <!-- 封禁 -->
          <div v-else-if="status === 'banned'" class="qr-state error">
            <div class="state-icon error">
              <i class="fa-solid fa-ban"></i>
            </div>
            <p class="state-text">{{ errorText }}</p>
          </div>

          <!-- 失败 -->
          <div v-else-if="status === 'failed'" class="qr-state error">
            <div class="state-icon error">
              <i class="fa-solid fa-circle-xmark"></i>
            </div>
            <p class="state-text">{{ errorText }}</p>
          </div>

          <!-- 过期 -->
          <div v-else-if="status === 'expired'" class="qr-state error">
            <div class="state-icon expired">
              <i class="fa-solid fa-clock"></i>
            </div>
            <p class="state-text">二维码已过期</p>
          </div>

          <!-- 错误 -->
          <div v-else-if="status === 'error'" class="qr-state error">
            <div class="state-icon error">
              <i class="fa-solid fa-triangle-exclamation"></i>
            </div>
            <p class="state-text">{{ errorText }}</p>
          </div>

          <!-- 重新生成按钮 -->
          <div v-if="isRetryable" class="qr-actions">
            <button class="retry-btn" @click="regenerate">
              <i class="fa-solid fa-rotate-right"></i> 重新生成
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import { ref, watch, nextTick, onBeforeUnmount, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import QRCode from 'qrcode'
import axios from 'axios'
import serverUrl, { assetsUrl } from '@/assets/BaseServerUrl.js'

export default {
  name: 'WxQrLogin',
  props: {
    visible: { type: Boolean, default: false },
    // 'login' = 扫码登录, 'bind' = 扫码绑定
    mode: { type: String, default: 'login' }
  },
  emits: ['close', 'success', 'bound', 'unbound'],
  setup(props, { emit }) {
    const router = useRouter()
    const loading = ref(false)
    const qrUrl = ref('')
    const qrCodeBase64 = ref('')
    const schemeUrl = ref('')
    const ticket = ref('')
    const status = ref('idle')
    const errorText = ref('')
    const successText = ref('')
    const countdown = ref(300)
    const qrCanvas = ref(null)
    const isMobile = ref(false)

    let pollTimer = null
    let countdownTimer = null

    const checkMobile = () => {
      isMobile.value = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768
    }

    const openWechatMiniApp = () => {
      if (schemeUrl.value) {
        window.location.href = schemeUrl.value
      } else {
        console.warn('微信小程序 Scheme URL 暂未获取到')
      }
    }

    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', checkMobile)
    })

    const isFinalState = computed(() => {
      return ['success', 'unbound', 'banned', 'failed', 'expired', 'error'].includes(status.value)
    })

    const isRetryable = computed(() => {
      return ['unbound', 'banned', 'failed', 'expired', 'error'].includes(status.value)
    })

    const renderQrCode = async (url) => {
      await nextTick()
      if (qrCanvas.value) {
        try {
          await QRCode.toCanvas(qrCanvas.value, url, {
            width: 240,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' }
          })
        } catch (e) {
          console.error('QR code render failed:', e)
        }
      }
    }

    const generateQrCode = async () => {
      loading.value = true
      status.value = 'idle'
      errorText.value = ''
      successText.value = ''
      qrUrl.value = ''
      qrCodeBase64.value = ''
      schemeUrl.value = ''
      ticket.value = ''
      countdown.value = 300

      try {
        const endpoint = props.mode === 'bind'
          ? '/api/auth/wx/bind/generate'
          : '/api/auth/wx/qrlogin/generate'

        const token = localStorage.getItem('userToken')
        const headers = token ? { Authorization: `Bearer ${token}` } : {}

        const response = await axios.post(`${serverUrl}${endpoint}`, {}, { headers })

        if (response.data.success) {
          ticket.value = response.data.ticket
          status.value = 'pending'
          schemeUrl.value = response.data.urlLink || response.data.schemeUrl || ''

          if (response.data.qrCodeBase64) {
            qrCodeBase64.value = response.data.qrCodeBase64
          } else if (response.data.qrUrl) {
            qrUrl.value = response.data.qrUrl
            await renderQrCode(response.data.qrUrl)
          }

          startPolling()
          startCountdown()

          if (isMobile.value && schemeUrl.value) {
            openWechatMiniApp()
          }
        } else {
          status.value = 'error'
          errorText.value = response.data.error || '生成登录凭证失败'
        }
      } catch (error) {
        status.value = 'error'
        errorText.value = error?.response?.data?.error || '生成登录凭证失败，请稍后重试'
      } finally {
        loading.value = false
      }
    }

    const startPolling = () => {
      stopPolling()
      const pollEndpoint = props.mode === 'bind'
        ? '/api/auth/wx/bind/status'
        : '/api/auth/wx/qrlogin/status'

      pollTimer = setInterval(async () => {
        if (!ticket.value) return
        try {
          const response = await axios.get(`${serverUrl}${pollEndpoint}`, {
            params: { ticket: ticket.value }
          })
          const data = response.data
          status.value = data.status

          if (data.status === 'success') {
            stopPolling()
            stopCountdown()
            if (props.mode === 'login' && data.data) {
              // 登录成功 — 存储用户数据到 localStorage
              const userData = data.data
              localStorage.setItem('userInfo', JSON.stringify(userData))
              localStorage.setItem('userToken', userData.token)
              localStorage.setItem('lastLoginTime', new Date().toISOString())
              localStorage.setItem('isLoggedIn', 'true')

              successText.value = '微信登录成功！'
              emit('success', userData)

              // 1.5秒后跳转首页
              setTimeout(() => {
                emit('close')
                router.push('/')
                setTimeout(() => { window.location.reload() }, 100)
              }, 1500)
            } else if (props.mode === 'bind') {
              // 绑定成功
              successText.value = '微信绑定成功！'
              emit('bound', data.data)
              setTimeout(() => { emit('close') }, 1500)
            }
          } else if (data.status === 'unbound') {
            stopPolling()
            stopCountdown()
            errorText.value = data.error || '该微信账号暂未绑定云语账号'
            if (props.mode === 'login' && data.bindToken) {
              emit('unbound', data.bindToken)
            }
          } else if (data.status === 'banned') {
            stopPolling()
            stopCountdown()
            errorText.value = data.error || '账号已被封禁'
          } else if (data.status === 'failed') {
            stopPolling()
            stopCountdown()
            errorText.value = data.error || '操作失败'
          } else if (data.status === 'expired') {
            stopPolling()
            stopCountdown()
          }
        } catch (e) {
          // 轮询失败，静默重试
        }
      }, 2000)
    }

    const startCountdown = () => {
      stopCountdown()
      countdownTimer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          stopCountdown()
          stopPolling()
          status.value = 'expired'
        }
      }, 1000)
    }

    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
    }

    const stopCountdown = () => {
      if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null }
    }

    const regenerate = () => {
      generateQrCode()
    }

    const close = () => {
      stopPolling()
      stopCountdown()
      emit('close')
    }

    // 监听 visible 变化
    watch(() => props.visible, (newVal) => {
      if (newVal) {
        generateQrCode()
      } else {
        stopPolling()
        stopCountdown()
      }
    })

    onBeforeUnmount(() => {
      stopPolling()
      stopCountdown()
      window.removeEventListener('resize', checkMobile)
    })

    return {
      loading, qrUrl, qrCodeBase64, schemeUrl, status, errorText, successText,
      countdown, qrCanvas, isFinalState, isRetryable, isMobile,
      openWechatMiniApp, regenerate, close, assetsUrl
    }
  }
}
</script>

<style scoped>
/* 移动端跳转微信小程序样式 */
.mobile-jump-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 8px 8px;
  width: 100%;
}

.mobile-wx-badge {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(7, 193, 96, 0.1);
  color: #07c160;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
  margin-bottom: 20px;
  box-shadow: 0 8px 24px rgba(7, 193, 96, 0.18);
  animation: pulseWx 2s infinite;
}

@keyframes pulseWx {
  0% { transform: scale(1); box-shadow: 0 8px 24px rgba(7, 193, 96, 0.18); }
  50% { transform: scale(1.05); box-shadow: 0 12px 30px rgba(7, 193, 96, 0.3); }
  100% { transform: scale(1); box-shadow: 0 8px 24px rgba(7, 193, 96, 0.18); }
}

.mobile-jump-tip {
  font-size: 15px;
  color: #1d1d1f;
  margin: 0 0 24px 0;
  font-weight: 500;
  line-height: 1.5;
}

.dark-theme .mobile-jump-tip {
  color: #f5f5f7;
}

.mobile-jump-btn {
  width: 100%;
  height: 48px;
  background: #07c160;
  color: #ffffff;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(7, 193, 96, 0.35);
  transition: all 0.25s ease;
}

.mobile-jump-btn:active {
  transform: scale(0.98);
  background: #06ad56;
}

.wx-qr-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal, 9999);
}

.wx-qr-overlay-bg {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;
}

.wx-qr-modal {
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  padding: 32px;
  width: 100%;
  max-width: 380px;
  box-sizing: border-box;
}

.dark-theme .wx-qr-modal {
  background: rgba(28, 28, 30, 0.95);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
}

.wx-qr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.wx-qr-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1d1d1f;
}

.dark-theme .wx-qr-header h3 {
  color: #f5f5f7;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #86868b;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.close-btn:hover {
  color: #1d1d1f;
  background: rgba(0, 0, 0, 0.05);
}

.dark-theme .close-btn:hover {
  color: #f5f5f7;
  background: rgba(255, 255, 255, 0.1);
}

.wx-qr-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
}

.wx-qr-brand-logo {
  width: 40px;
  height: 40px;
  transition: transform 0.3s ease;
}

.wx-qr-brand-name {
  font-size: 18px;
  font-weight: 700;
  color: #1d1d1f;
  font-family: '爱点风雅黑', sans-serif;
  letter-spacing: 1px;
}

.dark-theme .wx-qr-brand-name {
  color: #f5f5f7;
}

.wx-qr-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 280px;
  justify-content: center;
}

.qr-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
}

.qr-state.loading {
  gap: 16px;
  color: #86868b;
}

.qr-state.loading i {
  font-size: 36px;
}

.qr-canvas-wrapper {
  position: relative;
  display: inline-block;
  padding: 12px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.qr-canvas {
  display: block;
  border-radius: 8px;
}

.qr-tip {
  margin: 16px 0 8px;
  font-size: 14px;
  color: #86868b;
}

.wx-brand {
  color: #07c160;
  font-weight: 600;
}

.qr-status-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #86868b;
  padding: 6px 14px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.04);
}

.qr-status-bar.pending i {
  color: #ff9500;
}

.state-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.state-icon.success { color: #34c759; }
.state-icon.error { color: #ff3b30; }
.state-icon.expired { color: #ff9500; }

.state-text {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.dark-theme .state-text {
  color: #f5f5f7;
}

.state-hint {
  font-size: 13px;
  color: #86868b;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;
}

.qr-actions {
  margin-top: 20px;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: rgba(7, 193, 96, 0.08);
  color: #07c160;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.retry-btn:hover {
  background: rgba(7, 193, 96, 0.15);
  border-color: rgba(7, 193, 96, 0.3);
}

.dark-theme .retry-btn {
  background: rgba(7, 193, 96, 0.1);
  border-color: rgba(255, 255, 255, 0.1);
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .wx-qr-modal,
.modal-leave-active .wx-qr-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .wx-qr-modal,
.modal-leave-to .wx-qr-modal {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}

@media (max-width: 480px) {
  .wx-qr-modal {
    padding: 24px 20px;
    max-width: calc(100vw - 40px);
  }

  .qr-canvas-wrapper {
    padding: 8px;
  }
}

/* Scanned state styles */
.scanned-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 240px;
  height: 240px;
  background: rgba(7, 193, 96, 0.05);
  border-radius: 16px;
  border: 1px dashed rgba(7, 193, 96, 0.3);
  box-sizing: border-box;
  animation: scaleUp 0.3s ease-out;
}

.scanned-icon {
  font-size: 54px;
  color: #07c160;
  margin-bottom: 16px;
}

.scanned-text {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 6px 0;
}

.scanned-subtext {
  font-size: 13px;
  color: #86868b;
  margin: 0;
}

.dark-theme .scanned-text {
  color: #f5f5f7;
}

.dark-theme .scanned-box {
  background: rgba(7, 193, 96, 0.08);
  border-color: rgba(7, 193, 96, 0.4);
}

@keyframes scaleUp {
  0% { transform: scale(0.9); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
</style>
